import { useEffect, useState } from "react";
import { trackEvent } from "@/lib/audit";

/**
 * Danger Play Mode — when the ball is in a high-risk situation (xG >= 0.55,
 * shot on target, penalty, dangerous free kick, VAR goal review, dangerous
 * attack), non-essential overlays (notably polls) are minimized so the
 * pitch and ball trajectory stay visible.
 *
 * PR — Critical Match State Audit: adds a continuous, signal-based
 * predictive evaluator (`evaluateThreatWindow`) so Goal Protection
 * engages BEFORE the shot/goal, not just after `inDanger` toggles.
 */

export type DangerPlayReason =
  | "xg_high"
  | "shot_on_target"
  | "penalty"
  | "free_kick_dangerous"
  | "var_goal_check"
  | "dangerous_attack"
  | "sustained_pressure"
  | "imminent_shot";

const EVT = "vs:danger_play_mode";
let activeUntil = 0;
let activeReason: DangerPlayReason | null = null;
let lastPressureScore = 0;
let lastXgThreat = 0;
let lastMomentum = 0;

export function activateDangerPlay(reason: DangerPlayReason, durationMs = 6000) {
  if (typeof window === "undefined") return;
  // Extend window: never shorten an already-longer protection.
  const newUntil = Date.now() + durationMs;
  if (newUntil <= activeUntil) {
    // Already protected longer; just refresh reason so UI labels stay current.
    activeReason = reason;
    return;
  }
  activeUntil = newUntil;
  activeReason = reason;
  void trackEvent("danger_play_activated", { reason, durationMs });
  window.dispatchEvent(new CustomEvent(EVT, { detail: { until: activeUntil, reason } }));
  window.setTimeout(() => {
    if (Date.now() >= activeUntil) {
      activeReason = null;
      void trackEvent("danger_play_ended", { reason });
      window.dispatchEvent(new CustomEvent(EVT, { detail: { until: 0, reason: null } }));
    }
  }, durationMs + 50);
}

export function useDangerPlay(): { active: boolean; reason: DangerPlayReason | null } {
  const [state, setState] = useState<{ active: boolean; reason: DangerPlayReason | null }>({
    active: activeUntil > Date.now(),
    reason: activeReason,
  });
  useEffect(() => {
    const handler = (e: Event) => {
      const d = (e as CustomEvent<{ until: number; reason: DangerPlayReason | null }>).detail;
      setState({ active: d.until > Date.now(), reason: d.reason });
    };
    window.addEventListener(EVT, handler);
    return () => window.removeEventListener(EVT, handler);
  }, []);
  return state;
}

// -------------------------------------------------------------------------
// Predictive threat evaluator
// -------------------------------------------------------------------------

export interface ThreatSignals {
  /** 0..1 momentum from sim feed (sustained attack progression). */
  momentum: number;
  /** Ball x in pitch coords (sim uses ~-32..32, attacking third > ~16). */
  ballX: number;
  /** True when sim flags ball in dangerous zone (ball.x > 26 in this sim). */
  inDanger: boolean;
  /** True during the 2s shot window before a goal. */
  isShot: boolean;
  /** True during the goal celebration window. */
  isGoal: boolean;
  /** Free kick from attacking third. */
  freeKick: boolean;
  /** Live xG of the current chance (0..1). */
  xgPct: number;
  /** Count of players currently sprinting (proxy for pressure). */
  sprintingCount: number;
}

export interface ThreatDecision {
  pressureScore: number; // 0..1
  xgThreat: number; // 0..1
  reason: DangerPlayReason | null;
  durationMs: number; // 0 if no activation
}

/**
 * Pure evaluator — no side effects, easy to unit test.
 * Tuned for the in-app pro simulation feed:
 *   - attacking third begins around ballX > 16
 *   - inDanger at ballX > 26
 *   - momentum saturates near 1.0 by t≈50
 *   - goal occurs at t=60 (isShot fires t∈[58,60))
 */
export function computeThreat(s: ThreatSignals): ThreatDecision {
  // Pressure composite: weighted blend.
  const finalThirdBoost = Math.max(0, Math.min(1, (s.ballX - 16) / 16));
  const sprintBoost = Math.min(1, s.sprintingCount / 6);
  const dangerBoost = s.inDanger ? 1 : 0;
  const pressureScore = Math.min(
    1,
    s.momentum * 0.45 +
      finalThirdBoost * 0.25 +
      sprintBoost * 0.15 +
      dangerBoost * 0.15,
  );
  const xgThreat = Math.min(1, s.xgPct);
  lastPressureScore = pressureScore;
  lastXgThreat = xgThreat;
  lastMomentum = s.momentum;

  // Decision ladder — highest priority first.
  if (s.isGoal) return { pressureScore, xgThreat, reason: "shot_on_target", durationMs: 5_000 };
  if (s.isShot) return { pressureScore, xgThreat, reason: "imminent_shot", durationMs: 8_000 };
  if (xgThreat >= 0.65) return { pressureScore, xgThreat, reason: "xg_high", durationMs: 5_000 };
  if (s.inDanger && s.momentum >= 0.75)
    return { pressureScore, xgThreat, reason: "dangerous_attack", durationMs: 5_000 };
  if (s.freeKick && s.ballX > 16)
    return { pressureScore, xgThreat, reason: "free_kick_dangerous", durationMs: 5_000 };
  // SPONSOR VISIBILITY FIX — tighten sustained-pressure trigger so Goal
  // Protection no longer suppresses the full sponsor card during ordinary
  // attacking phases. Requires both very high pressure AND the ball in the
  // danger zone.
  if (pressureScore >= 0.88 && s.inDanger)
    return { pressureScore, xgThreat, reason: "sustained_pressure", durationMs: 3_000 };
  return { pressureScore, xgThreat, reason: null, durationMs: 0 };
}

/**
 * Stateful entry-point: evaluates threat AND activates Goal Protection
 * by extending the danger-play window when a reason is returned.
 */
export function evaluateThreatWindow(s: ThreatSignals): ThreatDecision {
  const d = computeThreat(s);
  if (d.reason && d.durationMs > 0) activateDangerPlay(d.reason, d.durationMs);
  return d;
}

export interface DangerSnapshot {
  active: boolean;
  reason: DangerPlayReason | null;
  msRemaining: number;
  pressureScore: number;
  xgThreat: number;
  momentum: number;
}

/** For the debug overlay — not reactive. */
export function getDangerSnapshot(): DangerSnapshot {
  return {
    active: activeUntil > Date.now(),
    reason: activeReason,
    msRemaining: Math.max(0, activeUntil - Date.now()),
    pressureScore: lastPressureScore,
    xgThreat: lastXgThreat,
    momentum: lastMomentum,
  };
}